// ─────────────────────────────────────────────────────────────────────────────
// THE SHOW-SIDE ZERO-LOGGING FENCE (Ticket 6 deliverable 6 / exit-test #3).
//
// The invariant: "the show-side request log for the session contains zero
// per-user preference data." The runtime tests cannot cover this — it is a
// property of the FRONTEND's request path, so it is enforced here.
//
// HOW IT IS GUARANTEED — structurally, in three layers:
//
//   1. NO SHOW-SIDE API EXISTS. The show is a static bundle. There is no
//      endpoint on this origin that accepts a profile, a ranking request, a
//      chip press, or a card impression. Nothing can be logged that is never
//      sent. `config.ts` has no SHOW_API_BASE by construction.
//
//   2. THE ONLY SHOW-SIDE REQUEST IS AN UNDECORATED STATIC GET. The candidate
//      set is fetched with no query string, no custom headers, no credentials,
//      no body. The show's access log therefore holds exactly what a CDN log
//      holds for any anonymous visitor: IP, timestamp, path, UA. That is the
//      "version N was fetched K times" world the exchange-candidate schema
//      describes. `assertBareShowFetch` below is the enforcement point.
//
//   3. RANKING NEVER TOUCHES THIS ORIGIN. `POST /app/rank-candidates` goes to
//      the fan's own runtime. The candidate set travels TO the fan's agent;
//      the profile stays in the fan's DO. The show never sees an ordering, a
//      why-string, or a chip.
//
// This module is the runtime tripwire for (2) and (3). It is not decoration:
// `rankCandidates` and `patchInterests` call `assertRuntimeOrigin`, and
// `loadCandidateSet` calls `assertBareShowFetch`, so a future edit that
// smuggles preference data into a show-side request fails loudly in dev and in
// test rather than silently leaking.
// ─────────────────────────────────────────────────────────────────────────────

import {AGENT_RUNTIME_BASE_URL} from './config'

export class ShowFenceViolation extends Error {
  constructor(message: string) {
    super(`show-side fence violation: ${message}`)
    this.name = 'ShowFenceViolation'
  }
}

/** Origin of a URL, tolerating relative URLs (which are same-origin = the show). */
function originOf(url: string, base?: string): string {
  return new URL(url, base ?? 'http://show.invalid').origin
}

/**
 * Assert a personalized call is addressed to the fan's OWN agent runtime and
 * not to the show. Called before every request that carries a bearer or a
 * preference payload.
 */
export function assertRuntimeOrigin(url: string): void {
  const target = originOf(url)
  const runtime = new URL(AGENT_RUNTIME_BASE_URL).origin
  if (target !== runtime) {
    throw new ShowFenceViolation(
      `personalized request addressed to ${target}, expected the fan's agent runtime ${runtime}. ` +
        `Ranking and interest writes must never reach the show.`,
    )
  }
}

/**
 * Assert a show-origin fetch carries NOTHING that could identify a fan or
 * describe a preference: no query string, no fragment, no headers, no body,
 * no credentials. A bare GET.
 */
export function assertBareShowFetch(url: string, init?: RequestInit): void {
  const parsed = new URL(url, 'http://show.invalid')
  if (parsed.search) {
    throw new ShowFenceViolation(
      `show-side fetch carries a query string (${parsed.search}). A query string is a log line — ` +
        `per-user data must never be encoded in a show-side URL.`,
    )
  }
  const method = (init?.method ?? 'GET').toUpperCase()
  if (method !== 'GET') {
    throw new ShowFenceViolation(`show-side fetch uses ${method}; only bare GET is permitted.`)
  }
  if (init?.body != null) {
    throw new ShowFenceViolation('show-side fetch carries a body; the show accepts no per-user payload.')
  }
  if (init?.credentials && init.credentials !== 'omit') {
    throw new ShowFenceViolation(
      `show-side fetch sets credentials="${init.credentials}"; the show must not receive cookies.`,
    )
  }
  const headers = init?.headers
  if (headers && Object.keys(headersToObject(headers)).length > 0) {
    throw new ShowFenceViolation(
      'show-side fetch sets custom headers; a bare asset GET is the entire permitted show-side request path.',
    )
  }
}

function headersToObject(headers: HeadersInit): Record<string, string> {
  if (headers instanceof Headers) return Object.fromEntries(headers.entries())
  if (Array.isArray(headers)) return Object.fromEntries(headers)
  return {...headers}
}
